import { useState } from "react";
import { api, apiErr } from "@/lib/apiClient";
import LegalShell from "@/components/public/LegalShell";
import { useLang } from "@/i18n/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Loader2, CheckCircle2 } from "lucide-react";

export default function Contacto() {
  const { t } = useLang();
  const C = t.contact;
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      setForm({ name: "", email: "", subject: "", message: "" });
      toast.success(C.successTitle);
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <LegalShell testid="page-contacto" eyebrow={C.eyebrow} title={C.title} intro={C.intro}>
      <div className="grid md:grid-cols-5 gap-10">
        <div className="md:col-span-2 space-y-6">
          <div className="flex items-start gap-3">
            <Mail className="w-5 h-5 text-foreground mt-0.5" />
            <div>
              <div className="font-bold">{C.email}</div>
              <a href="mailto:hola@domifood.app" className="text-sm text-muted-foreground hover:text-foreground" data-testid="contact-email">hola@domifood.app</a>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Phone className="w-5 h-5 text-foreground mt-0.5" />
            <div>
              <div className="font-bold">{C.phone}</div>
              <span className="text-sm text-muted-foreground">+1 (555) 010-2030</span>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <MapPin className="w-5 h-5 text-foreground mt-0.5" />
            <div>
              <div className="font-bold">{C.office}</div>
              <span className="text-sm text-muted-foreground">123 Olive Street, Springfield</span>
            </div>
          </div>
        </div>

        <div className="md:col-span-3">
          {sent ? (
            <div className="rounded-2xl border-2 border-emerald-200 bg-emerald-50 p-8 text-center" data-testid="contact-success">
              <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto mb-3" />
              <h3 className="font-admin font-bold text-xl">{C.successTitle}</h3>
              <p className="text-sm text-muted-foreground mt-1">{C.successDesc}</p>
              <Button className="mt-5 rounded-full font-bold" onClick={() => setSent(false)} data-testid="contact-again-btn">{C.again}</Button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4 rounded-2xl border border-border p-6">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="c-name">{C.name}</Label>
                  <Input id="c-name" required value={form.name} onChange={set("name")} data-testid="contact-name-input" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="c-email">{C.email}</Label>
                  <Input id="c-email" type="email" required value={form.email} onChange={set("email")} data-testid="contact-email-input" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="c-subject">{C.subject}</Label>
                <Input id="c-subject" value={form.subject} onChange={set("subject")} placeholder={C.subjectPh} data-testid="contact-subject-input" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="c-message">{C.message}</Label>
                <Textarea id="c-message" required rows={5} value={form.message} onChange={set("message")} data-testid="contact-message-input" />
              </div>
              <Button type="submit" disabled={sending} className="rounded-full font-bold px-8" data-testid="contact-submit-btn">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : C.send}
              </Button>
            </form>
          )}
        </div>
      </div>
    </LegalShell>
  );
}
